/**
 * useVariableSuggestions Hook
 *
 * Manages variable autocomplete suggestions for workflow inputs.
 * Detects {{ trigger pattern and filters available variables.
 */

import { useState, useMemo, useCallback } from 'react';
import type { AvailableContext } from '../utils/node-schemas';
import type { ContextVariable, VariableTriggerResult } from '../types/variables';
import {
  getAvailableVariables,
  filterVariables,
  groupVariablesByCategory,
} from '../utils/context-variables';

interface UseVariableSuggestionsOptions {
  /** Available context from workflow node */
  availableContext: AvailableContext;
  /** Current text value */
  value: string;
  /** Current cursor position */
  cursorPosition: number;
}

interface UseVariableSuggestionsReturn {
  /** Whether suggestions should be shown */
  showSuggestions: boolean;
  /** Filtered list of suggestions */
  suggestions: ContextVariable[];
  /** Suggestions grouped by category */
  groupedSuggestions: Record<string, ContextVariable[]>;
  /** Current search term (text after {{) */
  searchTerm: string;
  /** Trigger info for replacement */
  triggerInfo: VariableTriggerResult | null;
  /** Index of currently selected suggestion */
  selectedIndex: number;
  /** Move selection up */
  selectPrevious: () => void;
  /** Move selection down */
  selectNext: () => void;
  /** Reset selection */
  resetSelection: () => void;
  /** Set selection by index */
  setSelectedIndex: (index: number) => void;
  /** Insert variable at trigger position */
  insertVariable: (variable: ContextVariable) => string;
  /** Close suggestions */
  closeSuggestions: () => void;
}

/**
 * Detect if user is typing a variable trigger ({{)
 */
function detectTrigger(text: string, cursorPosition: number): VariableTriggerResult {
  // Look backwards from cursor for {{
  const textBeforeCursor = text.slice(0, cursorPosition);

  // Find the last {{ before cursor
  const lastTriggerIndex = textBeforeCursor.lastIndexOf('{{');

  if (lastTriggerIndex === -1) {
    return { isTriggered: false, searchTerm: '', triggerStart: -1, triggerEnd: -1 };
  }

  // Check if there's a closing }} between {{ and cursor
  const textAfterTrigger = textBeforeCursor.slice(lastTriggerIndex + 2);
  if (textAfterTrigger.includes('}}')) {
    return { isTriggered: false, searchTerm: '', triggerStart: -1, triggerEnd: -1 };
  }

  // Extract search term (text between {{ and cursor)
  const searchTerm = textAfterTrigger;

  return {
    isTriggered: true,
    searchTerm,
    triggerStart: lastTriggerIndex,
    triggerEnd: cursorPosition,
  };
}

/**
 * Hook for managing variable autocomplete suggestions
 */
export function useVariableSuggestions(
  options: UseVariableSuggestionsOptions
): UseVariableSuggestionsReturn {
  const { availableContext, value, cursorPosition } = options;

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [forceClosed, setForceClosed] = useState(false);

  // Detect trigger and get search term
  const triggerInfo = useMemo(() => {
    if (forceClosed) {
      return { isTriggered: false, searchTerm: '', triggerStart: -1, triggerEnd: -1 };
    }
    return detectTrigger(value, cursorPosition);
  }, [value, cursorPosition, forceClosed]);

  // Get available variables based on context
  const availableVariables = useMemo(() => {
    return getAvailableVariables(availableContext);
  }, [availableContext]);

  // Filter suggestions based on search term
  const suggestions = useMemo(() => {
    if (!triggerInfo.isTriggered) return [];
    return filterVariables(availableVariables, triggerInfo.searchTerm);
  }, [triggerInfo.isTriggered, triggerInfo.searchTerm, availableVariables]);

  // Group suggestions by category
  const groupedSuggestions = useMemo(() => {
    return groupVariablesByCategory(suggestions);
  }, [suggestions]);

  // Reset selection when suggestions change
  const resetSelection = useCallback(() => {
    setSelectedIndex(0);
  }, []);

  // Navigate suggestions
  const selectPrevious = useCallback(() => {
    setSelectedIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
  }, [suggestions.length]);

  const selectNext = useCallback(() => {
    setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
  }, [suggestions.length]);

  // Insert variable into text
  const insertVariable = useCallback(
    (variable: ContextVariable): string => {
      if (!triggerInfo.isTriggered) return value;

      const before = value.slice(0, triggerInfo.triggerStart);
      const after = value.slice(triggerInfo.triggerEnd);

      // Reset force closed
      setForceClosed(false);

      return `${before}${variable.fullPath}${after}`;
    },
    [value, triggerInfo]
  );

  // Close suggestions manually
  const closeSuggestions = useCallback(() => {
    setForceClosed(true);
    // Reset after a short delay to allow re-trigger
    setTimeout(() => setForceClosed(false), 100);
  }, []);

  return {
    showSuggestions: triggerInfo.isTriggered && suggestions.length > 0,
    suggestions,
    groupedSuggestions,
    searchTerm: triggerInfo.searchTerm,
    triggerInfo: triggerInfo.isTriggered ? triggerInfo : null,
    selectedIndex,
    selectPrevious,
    selectNext,
    resetSelection,
    setSelectedIndex,
    insertVariable,
    closeSuggestions,
  };
}

export default useVariableSuggestions;
